// ── Welle D3c: gemeinsamer Lader der Heute-Widgets ──
// Unterscheidet „leer“ von „nicht geladen“: schlägt eine Anfrage fehl, liefert der Hook
// fehler (die alte Variante mit leseFallback machte daraus eine leere Liste — D3b-Befund:
// „Termine“ zeigte einmal „keine Termine“, obwohl nur die Anfrage scheiterte).
// Gleiche Pfade teilen sich eine Anfrage (z. B. /api/aktionen/faellig für Lerngruppen und
// „Ausstehende Aktionen“); neu() bzw. ladeNeu(pfad) holt frisch und informiert alle Nutzer.
import React, { useCallback, useEffect, useState } from 'react';
import { holeToken } from '../../../lib/sitzung';
import { apiGet } from '../../../lib/api';
import { MIcon } from '../../../lib/micon';

type Eintrag = { daten?: unknown; fehler?: boolean; laeuft?: Promise<void>; zeit?: number };
const speicher = new Map<string, Eintrag>();
const hoerer = new Map<string, Set<() => void>>();
const FRISCH_MS = 15_000; // innerhalb dieser Zeit teilen sich Widgets dieselbe Antwort

// Zwischenspeicher je Lehrkraft (Mandanten-Trennung bei Abmelden/Anmelden im selben Tab)
function lehrerKey(): string {
  try {
    const teil = holeToken()?.split('.')[1];
    return teil ? String(JSON.parse(window.atob(teil.replace(/-/g, '+').replace(/_/g, '/')))?.id ?? '') : '';
  } catch { return ''; }
}
const k = (pfad: string) => `${lehrerKey()}|${pfad}`;

function melde(pfad: string) { hoerer.get(pfad)?.forEach(f => f()); }

function hole(pfad: string, erzwingen = false) {
  const sk = k(pfad);
  const e = speicher.get(sk) ?? {};
  if (e.laeuft) return e.laeuft;
  if (!erzwingen && e.zeit && Date.now() - e.zeit < FRISCH_MS && !e.fehler) return Promise.resolve();
  const laeuft = apiGet<unknown>(pfad)
    .then(d => { speicher.set(sk, { daten: d, fehler: false, zeit: Date.now() }); })
    .catch(() => { speicher.set(sk, { daten: speicher.get(sk)?.daten, fehler: true, zeit: Date.now() }); })
    .finally(() => melde(pfad));
  speicher.set(sk, { ...e, laeuft });
  return laeuft;
}

/** Frisch laden (z. B. nach dem Abhaken einer Aufgabe oder einem neuen schulfreien Tag) */
export function ladeNeu(pfad: string) { return hole(pfad, true); }

/** Daten lokal ersetzen (optimistische Änderung), ohne neu zu laden */
export function setzeDaten<T>(pfad: string, f: (alt: T | undefined) => T) {
  const sk = k(pfad);
  const e = speicher.get(sk) ?? {};
  speicher.set(sk, { ...e, daten: f(e.daten as T | undefined) });
  melde(pfad);
}

export type Geladen<T> = { daten: T | undefined; fehler: boolean; laedt: boolean; neu: () => void };

/**
 * Lädt pfad (null = nichts laden). daten bleibt beim Neuladen stehen; fehler nur, wenn
 * keine brauchbare Antwort da ist. intervallMs: regelmäßig neu laden (Standard: nein).
 */
export function useLader<T>(pfad: string | null, intervallMs = 0): Geladen<T> {
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!pfad) return;
    const f = () => setTick(t => t + 1);
    if (!hoerer.has(pfad)) hoerer.set(pfad, new Set());
    hoerer.get(pfad)!.add(f);
    void hole(pfad);
    const t = intervallMs ? window.setInterval(() => { void hole(pfad, true); }, intervallMs) : 0;
    return () => { hoerer.get(pfad)?.delete(f); if (t) window.clearInterval(t); };
  }, [pfad, intervallMs]);
  const neu = useCallback(() => { if (pfad) void hole(pfad, true); }, [pfad]);
  const e = pfad ? speicher.get(k(pfad)) : undefined;
  const daten = e?.daten as T | undefined;
  return { daten, fehler: !!e?.fehler && daten === undefined, laedt: !!e?.laeuft && daten === undefined, neu };
}

/** Einheitlicher Fehlerzustand im Widget */
export function LadeFehler({ neu, text = 'Konnte nicht geladen werden.' }: { neu: () => void; text?: string }) {
  return (
    <div className="ld-wfehler" role="status">
      <MIcon name="cloud_off" size={20} />
      <span>{text}</span>
      <button type="button" className="ld-wfehler__btn" onClick={neu}>Erneut versuchen</button>
    </div>
  );
}

/** Ruhiger Ladezustand (Skelett-Zeilen) */
export function LadeZeilen({ n = 2 }: { n?: number }) {
  return (
    <div className="ld-wladen" aria-busy="true" aria-label="Wird geladen">
      {Array.from({ length: n }).map((_, i) => <div key={i} className="ld-skeleton ld-wladen__zeile" />)}
    </div>
  );
}

/** Lade-/Fehler-Weiche: rendert Kinder nur mit Daten */
export function MitDaten<T>({ g, zeilen = 2, children }: { g: Geladen<T>; zeilen?: number; children: (d: T) => React.ReactNode }) {
  if (g.daten !== undefined) return <>{children(g.daten)}</>;
  if (g.fehler) return <LadeFehler neu={g.neu} />;
  return <LadeZeilen n={zeilen} />;
}
